import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Panel, PageTitle, StarDivider } from "@/components/common";
import { toast } from "sonner";

const ALGOS = [
  { k: "casual", l: "Casuale" }, { k: "average_kd", l: "Bilanciato per K/D" },
  { k: "competitive", l: "Fasce K/D" }, { k: "custom_score", l: "Rating personalizzato" },
];

export default function Impostazioni() {
  const location = useLocation();
  const preselectTid = location.state?.tid || "";
  const [tours, setTours] = useState([]);
  const [tid, setTid] = useState(preselectTid);
  const [tour, setTour] = useState(null);
  const [championships, setChampionships] = useState([]);

  useEffect(() => {
    api.get("/tournaments", { params: { templates: false } }).then(({ data }) => { setTours(data); setTid((cur) => cur || (data[0] ? data[0].id : "")); });
    api.get("/championships").then(({ data }) => setChampionships(data));
  }, []);
  useEffect(() => { if (tid) api.get(`/tournaments/${tid}`).then(({ data }) => setTour(data)); }, [tid]);

  const saveConfig = async () => {
    try {
      await api.put(`/tournaments/${tid}/config`, {
        scoring: tour.scoring,
        balancing: tour.balancing,
        best_of:
          tour.best_of == null ||
          Number(tour.best_of) === Number(tour.num_matches)
            ? null
            : tour.best_of,
        championship_ids: tour.championship_ids || (
          tour.championship_id ? [tour.championship_id] : []
        ),
        championship_id: (
          tour.championship_ids?.[0] ||
          tour.championship_id ||
          null
        ),
      });
      toast.success("Configurazione salvata");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const changePassword = async () => {
    try {
      await api.put("/auth/change-password", pwd);
      toast.success("Password aggiornata con successo");
      setPwd({ current_password: "", new_password: "" });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const setBal = (k, v) => setTour({ ...tour, balancing: { ...tour.balancing, [k]: v } });

  const getLobbyPositionCount = (tournament) => {
    if (!tournament) return 10;

    const teamSize = Number(tournament.max_players || 0);
    const mode = String(tournament.mode || "").trim().toLowerCase();
    const kdMode = String(tournament.kd_mode || "").trim().toLowerCase();

    const isResurgence =
      kdMode === "resurgence" ||
      mode.includes("ritorno") ||
      mode.includes("resurgence");

    const capacities = isResurgence
      ? { 1: 44, 2: 22, 3: 16, 4: 11 }
      : { 1: 150, 2: 75, 3: 50, 4: 38 };

    return capacities[teamSize] || 10;
  };

  const updateScoringPosition = (field, pos, value) => {
    const numericValue = Number(value);

    setTour({
      ...tour,
      scoring: {
        ...tour.scoring,
        [field]: {
          ...(tour.scoring[field] || {}),
          [String(pos)]: Number.isFinite(numericValue) ? numericValue : 0,
        },
      },
    });
  };

  const setPlacement = (pos, v) =>
    updateScoringPosition("placement_points", pos, v);

  const setMult = (pos, v) =>
    updateScoringPosition("kill_multipliers", pos, v);

  const setMvpMult = (pos, v) =>
    updateScoringPosition("mvp_kill_multipliers", pos, v);

  return (
    <div>
      <PageTitle title="Impostazioni" subtitle="Configurazione No-Code"
        action={<select value={tid} onChange={(e) => setTid(e.target.value)} className="bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none">
          {tours.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>} />

      {tour && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Panel className="p-6 lg:col-span-2" testid="championship-config">
            <StarDivider label="Associazione Campionati" className="mb-4" />
            <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
              Campionati
            </label>

            <div
              data-testid="championship-select"
              className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 space-y-2 disabled:opacity-50"
            >
              {championships
                .filter((championship) => {
                  const selectedIds = tour.championship_ids?.length
                    ? tour.championship_ids
                    : tour.championship_id
                      ? [tour.championship_id]
                      : [];

                  return (
                    ["draft", "active"].includes(championship.state) ||
                    selectedIds.includes(championship.id)
                  );
                })
                .map((championship) => {
                  const selectedIds = tour.championship_ids?.length
                    ? tour.championship_ids
                    : tour.championship_id
                      ? [tour.championship_id]
                      : [];

                  const checked = selectedIds.includes(championship.id);

                  return (
                    <label
                      key={championship.id}
                      className="flex items-center gap-2 font-rajdhani text-sm text-white cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={tour.state === "archived"}
                        onChange={() => {
                          const nextIds = checked
                            ? selectedIds.filter((id) => id !== championship.id)
                            : [...selectedIds, championship.id];

                          setTour({
                            ...tour,
                            championship_ids: nextIds,
                            championship_id: nextIds[0] || null,
                          });
                        }}
                      />
                      <span>
                        {championship.name}
                        {["completed", "archived"].includes(championship.state)
                          ? " (storico)"
                          : ""}
                      </span>
                    </label>
                  );
                })}

              {championships.length === 0 && (
                <p className="font-rajdhani text-xs text-[var(--silver)]">
                  Nessun Campionato disponibile
                </p>
              )}
            </div>

            <p className="font-rajdhani text-xs text-[var(--silver)] mt-2">
              Il torneo può contribuire a zero, uno o più Campionati contemporaneamente.
              La copia di un torneo resta sempre non associata.
            </p>
          </Panel>

          <Panel className="p-6" testid="balancing-config">
            <StarDivider label="Motore Bilanciamento" className="mb-4" />
            <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">Algoritmo</label>
            <select value={tour.balancing.algorithm} onChange={(e) => setBal("algorithm", e.target.value)} data-testid="algo-select"
              className="w-full mt-1 mb-4 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none">
              {ALGOS.map((a) => <option key={a.k} value={a.k}>{a.l}</option>)}</select>
            {tour.balancing.algorithm === "custom_score" && (<>
              <p className="font-rajdhani text-xs text-[var(--silver)] mb-2">Rating = (K/D × Peso K/D) + (Kills × Peso Kills) + (Wins × Peso Wins)</p>
              <div className="grid grid-cols-3 gap-3">
                {[["kd_weight", "Peso K/D"], ["kills_weight", "Peso Kills"], ["win_weight", "Peso Wins"]].map(([k, l]) => (
                  <div key={k}><label className="font-rajdhani text-[10px] uppercase text-[var(--silver)]">{l}</label>
                    <input type="number" step="0.1" value={tour.balancing[k]} onChange={(e) => setBal(k, +e.target.value)} className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none" /></div>
                ))}
              </div>
            </>)}
          </Panel>

          <Panel className="p-6" testid="scoring-config">
            <StarDivider label="Motore Scoring" className="mb-4" />

            <div className="mb-5">
              <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                Best-of
              </label>
              <select
                value={tour.best_of == null || Number(tour.best_of) === Number(tour.num_matches) ? "" : tour.best_of}
                onChange={(e) => setTour({ ...tour, best_of: e.target.value === "" ? null : +e.target.value })}
                data-testid="best-of-select"
                className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none"
              >
                <option value="">Tutte le {tour.num_matches} partite ({tour.num_matches} su {tour.num_matches})</option>
                {Array.from({ length: Math.max(tour.num_matches - 1, 0) }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>Migliori {n} su {tour.num_matches}</option>
                ))}
              </select>
              <p className="font-rajdhani text-xs text-[var(--silver)] mt-1">
                M = {tour.num_matches} partite previste · N = migliori partite conteggiate in classifica.
              </p>
            </div>

            <div className="mb-4">
              <div className="flex items-end justify-between gap-4 mb-3">
                <div>
                  <p className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                    Scoring per posizione lobby
                  </p>
                  <p className="font-rajdhani text-xs text-[var(--silver)] mt-1">
                    Configura punti e moltiplicatori per tutte le posizioni possibili
                    della lobby. I valori non ancora personalizzati usano 0 punti
                    piazzamento e moltiplicatore 1.0.
                  </p>
                </div>
                <span className="font-oswald text-xs text-[var(--gold)] whitespace-nowrap">
                  {getLobbyPositionCount(tour)} posizioni
                </span>
              </div>

              <div
                className="overflow-x-auto border border-[var(--steel)]/40"
                data-testid="scoring-position-table"
              >
                <div className="min-w-[660px]">
                  <div className="grid grid-cols-[80px_1fr_1fr_1fr] gap-2 px-3 py-2 bg-[#111] border-b border-[var(--steel)]/40">
                    <span className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                      Pos.
                    </span>
                    <span className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                      Punti piazzamento
                    </span>
                    <span className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                      Moltiplicatore Kill Team
                    </span>
                    <span className="font-rajdhani text-[10px] uppercase tracking-widest text-[#a78bfa]">
                      Moltiplicatore Kill MVP
                    </span>
                  </div>

                  <div className="max-h-[520px] overflow-y-auto">
                    {Array.from(
                      { length: getLobbyPositionCount(tour) },
                      (_, index) => index + 1
                    ).map((pos) => {
                      const key = String(pos);

                      const placementValue =
                        tour.scoring.placement_points?.[key] ?? 0;

                      const teamMultiplier =
                        tour.scoring.kill_multipliers?.[key] ??
                        tour.scoring.default_kill_multiplier ??
                        1.0;

                      const mvpMultiplier =
                        tour.scoring.mvp_kill_multipliers?.[key] ??
                        tour.scoring.default_mvp_kill_multiplier ??
                        1.0;

                      return (
                        <div
                          key={key}
                          className="grid grid-cols-[80px_1fr_1fr_1fr] gap-2 items-center px-3 py-2 border-b border-[var(--steel)]/20 last:border-b-0"
                          data-testid={`scoring-position-${pos}`}
                        >
                          <span className="font-oswald text-[var(--gold)]">
                            {pos}°
                          </span>

                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={placementValue}
                            onChange={(e) =>
                              setPlacement(key, e.target.value)
                            }
                            aria-label={`Punti piazzamento ${pos}`}
                            className="w-full bg-[#0a0a0a] border border-[var(--steel)]/50 px-2 py-1.5 text-center text-white text-sm focus:border-[var(--gold)] focus:outline-none"
                          />

                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            value={teamMultiplier}
                            onChange={(e) =>
                              setMult(key, e.target.value)
                            }
                            aria-label={`Moltiplicatore Kill Team ${pos}`}
                            className="w-full bg-[#0a0a0a] border border-[var(--steel)]/50 px-2 py-1.5 text-center text-white text-sm focus:border-[var(--gold)] focus:outline-none"
                          />

                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            value={mvpMultiplier}
                            onChange={(e) =>
                              setMvpMult(key, e.target.value)
                            }
                            aria-label={`Moltiplicatore Kill MVP ${pos}`}
                            className="w-full bg-[#0a0a0a] border border-[#a78bfa]/40 px-2 py-1.5 text-center text-white text-sm focus:border-[#a78bfa] focus:outline-none"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </Panel>

          <div className="lg:col-span-2 flex justify-end"><button data-testid="save-config-btn" onClick={saveConfig} className="btn-gold clip-beveled px-6 py-2.5">Salva Config Torneo</button></div>
        </div>
      )}

      <Panel className="p-6 mt-6" testid="change-password">
        <StarDivider label="Cambio Password" className="mb-4" />
        <div className="grid md:grid-cols-2 gap-4 max-w-2xl">
          <div>
            <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">Password Attuale</label>
            <input data-testid="current-password" type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
              className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none" />
          </div>
          <div>
            <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">Nuova Password</label>
            <input data-testid="new-password" type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
              className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none" />
          </div>
        </div>
        <button data-testid="change-password-btn" onClick={changePassword} className="btn-gold clip-beveled px-6 py-2.5 mt-5">Aggiorna Password</button>
      </Panel>
    </div>
  );
}
